import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Mail, Wrench, PaintRoller, Replace } from "lucide-react";
import { toast } from "sonner";
import { crud } from "../lib/api";
import { degerKaybiMerkez } from "../lib/degerKaybiApi";
import SearchableSelect from "./SearchableSelect";
import ParcaIslemEditor from "./deger-kaybi/ParcaIslemEditor";
import {
    blankParcaIslem,
    buildParcaPayload,
    islemEtiket,
    partSubtitleFromPart,
} from "../lib/parcaIslemUtils";
import { PANEL_SECTION } from "../lib/panelFormStyles";

const ISLEM_IKON = {
    DEGISIM: Replace,
    ONARIM: Wrench,
    BOYA: PaintRoller,
    KARMA: Wrench,
};

const blankPart = () => ({
    parca: null,
    ...blankParcaIslem(),
});

function getParcaLabel(p) {
    const nested =
        typeof p?.parca === "object" && p.parca
            ? p.parca.parca_adi || p.parca.ad
            : null;
    return (
        p?.parca_label ||
        p?.parca_adi ||
        p?.parca_detail?.parca_adi ||
        p?.parca_bilgisi?.parca_adi ||
        p?.parca_bilgisi?.ad ||
        nested ||
        (p?.parca != null ? `Parça #${p.parca}` : "Parça")
    );
}

function partSubtitle(p) {
    return partSubtitleFromPart(p);
}

function partIcon(p) {
    const tip = String(p?.islem_tipi || "").toUpperCase();
    if (tip === "KARMA" || (p?.onarim_seviyesi && p?.boya_tipi)) return Wrench;
    return ISLEM_IKON[tip] || Wrench;
}

/**
 * Hasar parçaları — değer kaybı kaydı altında.
 */
export default function DegerKaybiParcalariSection({
    parentId,
    hideSummary = false,
    embedded = false,
    onSummaryChange,
}) {
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(!!parentId);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState(blankPart());
    const [summary, setSummary] = useState(null);
    const [finalizeBusy, setFinalizeBusy] = useState(false);

    const isQueueMode = !parentId;

    const loadSummary = useCallback(async () => {
        if (!parentId) {
            setSummary(null);
            return;
        }
        try {
            const rec = await crud.retrieve("deger-kaybi", parentId);
            setSummary(rec || null);
            onSummaryChange?.(rec);
        } catch {
            setSummary(null);
        }
    }, [parentId, onSummaryChange]);

    const load = useCallback(async () => {
        if (!parentId) {
            setParts([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await crud.list("deger-kaybi-parcalari", {
                deger_kaybi: parentId,
                page_size: 200,
            });
            const items = Array.isArray(data) ? data : data.results || [];
            const filtered = items.filter((p) => {
                const fk = p?.deger_kaybi;
                if (typeof fk === "object") return fk?.id === parentId;
                return String(fk) === String(parentId);
            });
            const hydrated = await Promise.all(
                filtered.map(async (p) => {
                    if (getParcaLabel(p) !== "Parça" && !/^Parça #\d+$/.test(getParcaLabel(p))) {
                        return p;
                    }
                    const pid =
                        typeof p.parca === "object" ? p.parca?.id : p.parca;
                    if (!pid) return p;
                    try {
                        const parca = await crud.retrieve("arac-parcasi", pid);
                        const adi = parca?.parca_adi || "";
                        return {
                            ...p,
                            parca_label: adi || p.parca_label,
                            parca_adi: adi || p.parca_adi,
                        };
                    } catch {
                        return p;
                    }
                }),
            );
            setParts(hydrated);
        } catch {
            setParts([]);
        } finally {
            setLoading(false);
        }
    }, [parentId]);

    useEffect(() => {
        load();
        if (!hideSummary) loadSummary();
    }, [load, loadSummary, hideSummary]);

    async function refreshAfterChange() {
        await load();
        await loadSummary();
    }

    async function addPart() {
        if (!parentId) {
            toast.info("Önce değer kaybı kaydını oluşturun.");
            return;
        }
        if (!draft.parca) {
            toast.error("Parça seçin.");
            return;
        }
        setBusy(true);
        try {
            const islemPayload = buildParcaPayload(draft);
            if (!islemPayload) {
                toast.error("En az bir işlem seçin (onarım ve/veya boya).");
                return;
            }
            const payload = {
                deger_kaybi: parentId,
                parca: draft.parca,
                ...islemPayload,
            };
            await crud.create("deger-kaybi-parcalari", payload);
            toast.success("Parça eklendi");
            setDraft(blankPart());
            await refreshAfterChange();
        } catch (e) {
            const data = e?.response?.data;
            toast.error(
                data?.detail ||
                    (typeof data === "object"
                        ? JSON.stringify(data)
                        : null) ||
                    e?.message ||
                    "Eklenemedi",
            );
        } finally {
            setBusy(false);
        }
    }

    async function finalizeHesaplama() {
        if (!parentId) return;
        if (parts.length === 0) {
            toast.error("Önce en az bir hasar parçası ekleyin.");
            return;
        }
        setFinalizeBusy(true);
        try {
            const res = await degerKaybiMerkez.hesaplamayiTamamla(parentId);
            if (res?.ok) {
                toast.success(res.detail || "Hesaplama maili gönderildi.");
                await refreshAfterChange();
            } else {
                toast.warning(res?.detail || "Mail gönderilemedi.");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Hesaplama tamamlanamadı.",
            );
        } finally {
            setFinalizeBusy(false);
        }
    }

    async function deletePart(id) {
        if (!window.confirm("Parça silinsin mi?")) return;
        try {
            await crud.remove("deger-kaybi-parcalari", id);
            toast.success("Parça silindi");
            await refreshAfterChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Silinemedi.");
        }
    }

    if (isQueueMode) {
        return (
            <p className="text-xs text-amber-200/90 border border-amber-500/25 rounded-md px-3 py-2 bg-amber-500/10">
                Kaydı oluşturduktan sonra hasar parçalarını ekleyebilirsiniz.
            </p>
        );
    }

    const shellClass = embedded
        ? `${PANEL_SECTION} overflow-visible`
        : "rounded-lg border border-white/10 bg-[#121A2F] overflow-visible";

    return (
        <div className={shellClass}>
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-100">
                    Hasar parçaları
                </span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{parts.length} parça</span>
                    {!hideSummary && parts.length > 0 && (
                        <button
                            type="button"
                            onClick={finalizeHesaplama}
                            disabled={finalizeBusy}
                            className="h-8 px-3 rounded-md border border-[#D4AF37]/35 bg-[#D4AF37]/10 text-[11px] text-[#F3E5AB] hover:bg-[#D4AF37]/20 inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {finalizeBusy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Mail className="h-3.5 w-3.5" />
                            )}
                            Hesaplamayı tamamla
                        </button>
                    )}
                </div>
            </div>

            <p className="px-4 pt-2 text-[11px] text-slate-500 leading-relaxed">
                Parça ekledikçe baz ve gerçek DK anlık güncellenir. Hesaplama maili
                yalnızca <strong className="text-slate-400">Kaydet</strong> veya{" "}
                <strong className="text-slate-400">Hesaplamayı tamamla</strong> ile gider.
            </p>

            {!hideSummary && summary && (
                <div className="px-4 pt-3 grid grid-cols-2 gap-2">
                    <SummaryTile
                        label="Baz değer kaybı"
                        value={summary.hesaplanan_baz_deger_kaybi}
                    />
                    <SummaryTile
                        label="Gerçek değer kaybı"
                        value={summary.hesaplanan_real_deger_kaybi}
                        highlight
                    />
                </div>
            )}

            <div className="px-4 py-3">
                {loading ? (
                    <div className="py-4 text-center text-sm text-slate-400 flex justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Yükleniyor...
                    </div>
                ) : parts.length === 0 ? (
                    <p className="text-sm text-slate-400 py-1">
                        Henüz parça yok.
                    </p>
                ) : (
                    <ul className="divide-y divide-white/10 border border-white/10 rounded-md overflow-hidden mb-4">
                        {parts.map((p) => {
                            const Icn = partIcon(p);
                            return (
                                <li
                                    key={p.id}
                                    className="flex items-center gap-2 px-3 py-2 bg-[#0B0F19]/60 text-sm"
                                >
                                    <Icn className="h-4 w-4 text-[#F3E5AB] shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-slate-100 truncate">
                                            {getParcaLabel(p)}
                                        </p>
                                        <p className="text-[11px] text-slate-400">
                                            {partSubtitle(p)}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => deletePart(p.id)}
                                        className="h-8 w-8 flex items-center justify-center rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                    Yeni parça ekle
                </p>
                <div className="rounded-md border border-white/10 bg-[#0B0F19]/40 p-3 space-y-3">
                    <>
                        <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
                            Parça adı *
                        </label>
                        <SearchableSelect
                            value={draft.parca}
                            onChange={(id) => setDraft((d) => ({ ...d, parca: id }))}
                            resource="arac-parcasi"
                            placeholder="Parça ara ve seçin…"
                            testId="part-parca-select"
                            dropdownDirection="up"
                        />
                    </>
                    <ParcaIslemEditor
                        value={draft}
                        onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
                    />
                    <div className="flex justify-end pt-1">
                        <button
                            type="button"
                            onClick={addPart}
                            disabled={busy}
                            className="btn-gold h-9 px-4 rounded-md text-xs inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {busy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Plus className="h-3.5 w-3.5" />
                            )}
                            Parça ekle
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SummaryTile({ label, value, highlight }) {
    const display =
        value === null || value === undefined || value === ""
            ? "—"
            : new Intl.NumberFormat("tr-TR", {
                  style: "currency",
                  currency: "TRY",
                  maximumFractionDigits: 2,
              }).format(Number(value) || 0);
    return (
        <div
            className={
                "px-3 py-2 rounded-md border " +
                (highlight
                    ? "border-[rgba(15,118,110,0.35)] bg-[var(--mbs-primary-soft)]"
                    : "border-[var(--mbs-border)] bg-[var(--mbs-bg-soft)]")
            }
        >
            <div className="text-[10px] uppercase tracking-wide text-[var(--mbs-ink-3)]">
                {label}
            </div>
            <div
                className={
                    "font-mono-jb text-sm font-semibold mt-0.5 " +
                    (highlight ? "text-[var(--mbs-primary-hover)]" : "text-[var(--mbs-ink)]")
                }
            >
                {display}
            </div>
        </div>
    );
}
